import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { t } from 'i18next';
import { FloorplanRevision } from '@thive/fddm-types';

import { downloadFile } from '@/modules/common/helpers/browser';
import { useNotificationMessageApi } from '@/modules/Notifications/hooks/useNotificationMessageApi';
import { useAppMetaStore } from '@/modules/floorplanV2/store/project/projectState';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { logger } from '@common/logging/logger';
import { VehicleServiceFactory } from '@/modules/vehicles/service/VehicleServiceFactory';
import { FileServiceConnector } from '@/modules/api/FileApiConnector';
import { buildRevisionFileStorageBasePath } from '@/modules/common/helpers/buildRevisionFileStorageBasePath';
import { PROJECT_FILES_ROOT_CONTAINER } from '@/modules/api/constant';
import { referenceImageFileNameUtils } from '@/modules/referenceImage';
import { SimulationServiceFactory } from '@/modules/simulation/service/SimulationServiceFactory';

import { useZipFileProcessor } from './useZipFileProcessor';

export const useFloorplanImportExport = () => {
  const { createAssetZip } = useZipFileProcessor();
  const { getNotificationDetails } = useNotificationMessageApi();
  const { showSnackbar } = useSnackbarStore();

  const vehicleSvc = useMemo(() => VehicleServiceFactory.getInstance().createVehicleService(), []);
  const simulationSvc = useMemo(
    () => SimulationServiceFactory.getInstance().createSimulationService(),
    [],
  );

  /**
   * Get the current floor plan json
   */
  const getCurrentFloorplanFile = useCallback(async () => {
    const revision = useFloorplanDesignStore.getState().getModel();
    const { activeLayoutAlternative } = useAppMetaStore.getState();

    if (activeLayoutAlternative.status !== 'loaded') {
      logger.error('Expected activeLayoutAlternativeName to be defined');
      return;
    }

    return buildRevisionFile(revision, activeLayoutAlternative.data.name);
  }, []);

  const downloadFloorplan = useCallback(async () => {
    const fddmStore = useFloorplanDesignStore.getState();
    const vehicleTypes = fddmStore.getVehicleTypes();

    if (vehicleTypes.length === 0) {
      logger.error('No vehicles found');
      showSnackbar(t('floorplan_import_export.download_failed'));
      return;
    }

    try {
      vehicleSvc.cleanUpRedundantTypes();

      const { uvtFiles, uvtFileNames, kmMdbFiles, kmMdbFileNames } =
        await vehicleSvc.getVehicleTypeFiles(vehicleTypes);

      const { fmsConfigFiles, fmsConfigFileNames, orderProfileFiles, orderProfileFileNames } =
        await simulationSvc.getSimulationFiles();

      const floorplan = await getCurrentFloorplanFile();
      if (!floorplan) {
        logger.error('Failed to retrieve floorplan file');
        getNotificationDetails('500');
        showSnackbar(t('floorplan_import_export.download_failed'));
        return;
      }

      if (!kmMdbFiles.length && !uvtFiles.length) {
        logger.error('No vehicle assets to include in download');
        showSnackbar(t('floorplan_import_export.download_failed'));
        return;
      }

      const { referenceImage } = fddmStore.uxonly;
      let referenceImageFile: Blob | null = null;
      if (referenceImage) {
        const basePath = buildRevisionFileStorageBasePath();
        referenceImageFile = await FileServiceConnector.getInstance().getBlobFileByUrl(
          `${PROJECT_FILES_ROOT_CONTAINER}?filename=${basePath}/${referenceImage?.imageReference}`,
        );
      }

      const zipBlob = await createAssetZip(
        uvtFiles,
        uvtFileNames,
        kmMdbFiles,
        kmMdbFileNames,
        fmsConfigFiles,
        fmsConfigFileNames,
        orderProfileFiles,
        orderProfileFileNames,
        floorplan,
        referenceImage && referenceImageFile
          ? {
              blob: referenceImageFile,
              fileName: referenceImageFileNameUtils.getFilenameWithExtension(
                referenceImage.imageReference,
              ),
            }
          : undefined,
      );

      downloadFile(zipBlob, '.zip', `${floorplan.name.slice(0, -5)}.zip`);
    } catch (error) {
      logger.error('Failed to download floorplan', error);
      getNotificationDetails('500');
      showSnackbar(t('floorplan_import_export.download_failed'));
    }
  }, [
    showSnackbar,
    vehicleSvc,
    simulationSvc,
    getCurrentFloorplanFile,
    createAssetZip,
    getNotificationDetails,
  ]);

  return {
    downloadFloorplan,
    getCurrentFloorplanFile,
  };
};

/**
 * @description takes in a revision object and layout-alternative name and returns a File ready for download
 */
const buildRevisionFile = (revisionSource: FloorplanRevision, layoutAlternativeName: string) => {
  const revision = structuredClone(revisionSource);
  const { metaData, setup, design, uxonly } = revision;
  const { version } = metaData;

  const exportedRevision: FloorplanRevision = {
    metaData,
    setup,
    design,
    uxonly,
    floorplan: {
      stations: [],
      vehicleLayouts: [],
    },
    tracks: [],
  };

  const fileContents = JSON.stringify(exportedRevision);
  const fileName = `Floorplan_${layoutAlternativeName}-Version_${version}-${format(
    new Date(),
    'yyyyMMdd',
  )}.json`;

  return new File([fileContents], fileName, {
    type: 'application/json',
  });
};
